import React, { useState, useRef, useEffect } from 'react';
import { getVolume, speakText, setReplay } from './lzAudio';

// 10-level "Which one doesn't belong?" critical-thinking game (ages 2-5).
// Ollie the Owl reads every question, hint and explanation aloud (kids this
// age can't read yet). A wrong tap is a second chance, not a dead end - the
// child keeps going until they find it - but only first-try answers count
// toward the score/stars and the `correct` flag sent to onComplete.
// Each level has 3 variants, one picked at random per game, for replay.

const LEVELS = [
    {
        id: 1, topic: 'Colors', difficulty: 'Easy',
        variants: [
            { items: ['🔵', '🔵', '🔵', '🔴'], odd: 3, hint: 'Look at the colors!', explain: 'Red is different — the others are all blue!' },
            { items: ['🟢', '🟢', '🟡', '🟢'], odd: 2, hint: 'Look at the colors!', explain: 'Yellow is different — the others are all green!' },
            { items: ['🟣', '🟠', '🟣', '🟣'], odd: 1, hint: 'Look at the colors!', explain: 'Orange is different — the others are all purple!' },
        ],
    },
    {
        id: 2, topic: 'Shapes', difficulty: 'Easy',
        variants: [
            { items: ['⭕', '⭕', '🔷', '⭕'], odd: 2, hint: 'Look at the shapes!', explain: 'The diamond is different — the others are circles!' },
            { items: ['🟥', '🔺', '🟥', '🟥'], odd: 1, hint: 'Look at the shapes!', explain: 'The triangle is different — the others are squares!' },
            { items: ['⭐', '⭐', '⭐', '❤️'], odd: 3, hint: 'Look at the shapes!', explain: 'The heart is different — the others are stars!' },
        ],
    },
    {
        id: 3, topic: 'Animals vs things', difficulty: 'Easy',
        variants: [
            { items: ['🐶', '🐱', '🐰', '🚗'], odd: 3, hint: 'Which one is not an animal?', explain: 'The car is not an animal — dog, cat and bunny are animals!' },
            { items: ['🐮', '⚽', '🐷', '🐑'], odd: 1, hint: 'Which one is not an animal?', explain: 'The ball is a toy — cow, pig and sheep are animals!' },
            { items: ['📚', '🐸', '🦆', '🐔'], odd: 0, hint: 'Which one is not an animal?', explain: 'The book is not an animal — frog, duck and chicken are animals!' },
        ],
    },
    {
        id: 4, topic: 'Food', difficulty: 'Medium',
        variants: [
            { items: ['🍎', '🍊', '🍇', '🎈'], odd: 3, hint: 'Which one can you NOT eat?', explain: 'A balloon is not food — apple, orange and grapes are fruits!' },
            { items: ['🍌', '🧸', '🍓', '🍉'], odd: 1, hint: 'Which one can you NOT eat?', explain: 'A teddy bear is a toy — banana, strawberry and melon are fruits!' },
            { items: ['🥕', '🥦', '👟', '🌽'], odd: 2, hint: 'Which one can you NOT eat?', explain: 'A shoe is not food — carrot, broccoli and corn are vegetables!' },
        ],
    },
    {
        id: 5, topic: 'Where animals live', difficulty: 'Medium',
        variants: [
            { items: ['🐟', '🐬', '🐙', '🐶'], odd: 3, hint: 'Where do these animals live?', explain: 'The dog lives on land — fish, dolphin and octopus live in water!' },
            { items: ['🐠', '🦅', '🐦', '🦜'], odd: 0, hint: 'Which one cannot fly in the sky?', explain: 'The fish lives in water — eagle, bird and parrot fly in the sky!' },
            { items: ['🐄', '🐖', '🐳', '🐑'], odd: 2, hint: 'Which one does not live on a farm?', explain: 'The whale lives in the sea — cow, pig and sheep live on a farm!' },
        ],
    },
    {
        id: 6, topic: 'Things that go', difficulty: 'Medium',
        variants: [
            { items: ['🚗', '🚢', '✈️', '🍕'], odd: 3, hint: 'Which one can you NOT ride in?', explain: 'Pizza is food — car, ship and plane take us places!' },
            { items: ['🍩', '🚲', '🚌', '🚂'], odd: 0, hint: 'Which one can you NOT ride on?', explain: 'A donut is food — bike, bus and train take us places!' },
            { items: ['🚁', '🍦', '🚜', '🛵'], odd: 1, hint: 'Which one can you NOT ride on?', explain: 'Ice cream is food — helicopter, tractor and scooter take us places!' },
        ],
    },
    {
        id: 7, topic: 'Things that fly', difficulty: 'Hard',
        variants: [
            { items: ['🦋', '🐦', '✈️', '🐢'], odd: 3, hint: 'Which one can NOT fly?', explain: 'The turtle cannot fly — butterfly, bird and plane all fly!' },
            { items: ['🐝', '🐌', '🚁', '🦅'], odd: 1, hint: 'Which one can NOT fly?', explain: 'The snail cannot fly — bee, helicopter and eagle all fly!' },
            { items: ['🎈', '🪁', '🐘', '🦜'], odd: 2, hint: 'Which one can NOT fly?', explain: 'The elephant cannot fly — balloon, kite and parrot go up in the sky!' },
        ],
    },
    {
        id: 8, topic: 'Count the legs', difficulty: 'Hard',
        variants: [
            { items: ['🐶', '🐘', '🦁', '🐍'], odd: 3, hint: 'Count the legs!', explain: 'The snake has no legs — dog, elephant and lion have four legs!' },
            { items: ['🐈', '🐟', '🐎', '🐄'], odd: 1, hint: 'Count the legs!', explain: 'The fish has no legs — cat, horse and cow have four legs!' },
            { items: ['🐶', '🐱', '🐔', '🐮'], odd: 2, hint: 'Count the legs!', explain: 'The chicken has two legs — dog, cat and cow have four legs!' },
        ],
    },
    {
        id: 9, topic: 'Things that shine', difficulty: 'Expert',
        variants: [
            { items: ['💡', '🕯️', '⭐', '🍌'], odd: 3, hint: 'Which one does NOT make light?', explain: 'A banana does not glow — bulb, candle and star all give light!' },
            { items: ['☀️', '🧦', '🔦', '🕯️'], odd: 1, hint: 'Which one does NOT make light?', explain: 'A sock does not glow — sun, flashlight and candle all give light!' },
            { items: ['🥄', '🌙', '💡', '🔥'], odd: 0, hint: 'Which one does NOT make light?', explain: 'A spoon does not glow — moon, bulb and fire all give light!' },
        ],
    },
    {
        id: 10, topic: 'Alive or not?', difficulty: 'Expert',
        variants: [
            { items: ['🌻', '🐛', '🌱', '🪨'], odd: 3, hint: 'Which one does NOT grow?', explain: 'A rock is not alive — sunflower, caterpillar and sprout all grow!' },
            { items: ['🧱', '🌳', '🐞', '🐣'], odd: 0, hint: 'Which one does NOT grow?', explain: 'A brick is not alive — tree, ladybug and chick all grow!' },
            { items: ['🌷', '🐟', '✏️', '🦋'], odd: 2, hint: 'Which one does NOT grow?', explain: 'A pencil is not alive — tulip, fish and butterfly all grow!' },
        ],
    },
];

const DIFF_COLOR = { Easy: '#22C55E', Medium: '#F59E0B', Hard: '#F97316', Expert: '#8B5CF6' };
const CHEERS = ['Yes! You found it!', 'Great looking!', 'Super smart!', 'You got it!', 'Wonderful!'];
const CONFETTI = ['#FF3B30', '#FFCC00', '#28A745', '#1A6EFF', '#8B2BE2', '#FF69B4'];

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};
const pickVariant = (level) => level.variants[Math.floor(Math.random() * level.variants.length)];
const buildItems = (v) => shuffle(v.items.map((emoji, i) => ({ emoji, isOdd: i === v.odd, key: i })));
const starsFor = (pct) => (pct >= 100 ? 3 : pct >= 70 ? 2 : pct >= 40 ? 1 : 0);

const OddOneOut = ({ childId, childAge, onComplete, onFinish }) => {
    const [index, setIndex] = useState(0);
    const [variant, setVariant] = useState(() => pickVariant(LEVELS[0]));
    const [items, setItems] = useState(() => buildItems(variant));
    const [results, setResults] = useState([]); // true (first try) / false per finished level
    const [wrongKeys, setWrongKeys] = useState([]); // tapped wrong this level
    const [hiddenKey, setHiddenKey] = useState(null); // faded out by hint
    const [solved, setSolved] = useState(false);
    const [shakeKey, setShakeKey] = useState(null);
    const [bubble, setBubble] = useState('');
    const [streak, setStreak] = useState(0);
    const [finished, setFinished] = useState(false);
    const [round, setRound] = useState(0); // remount key for entry animations

    const audioCtx = useRef(null);
    const levelStart = useRef(Date.now());
    const timers = useRef([]);
    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

    const level = LEVELS[index];

    useEffect(() => {
        setReplay(() => (finished ? 'Great thinking! Tap play again for more.' : solved ? variant.explain : bubble));
        return () => setReplay(null);
    }, [finished, solved, variant, bubble]);

    // ─── Sound ──────────────────────────────────────────────────────────────
    const tone = (freq, delayMs = 0, gain = 0.15, dur = 0.18, type = 'sine') => {
        const vol = getVolume();
        if (!vol) return;
        try {
            if (!audioCtx.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return;
                audioCtx.current = new Ctx();
            }
            const ctx = audioCtx.current;
            if (ctx.state === 'suspended') ctx.resume();
            const t = ctx.currentTime + delayMs / 1000;
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = type;
            osc.frequency.value = freq;
            g.gain.setValueAtTime(gain * vol, t);
            g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
            osc.connect(g).connect(ctx.destination);
            osc.start(t);
            osc.stop(t + dur + 0.02);
        } catch (e) { /* ignore */ }
    };
    const say = (text) => speakText(text);
    const playCorrect = () => [523, 659, 784].forEach((f, i) => tone(f, i * 120));
    const playWrong = () => tone(180, 0, 0.1, 0.25, 'triangle');
    const playFinish = () => [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 130, 0.16, 0.25));

    const QUESTION = 'Which one is different?';

    useEffect(() => {
        setBubble(QUESTION);
        const t = setTimeout(() => say(QUESTION), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, round]);

    useEffect(() => () => {
        timers.current.forEach(clearTimeout);
        try { window.speechSynthesis?.cancel(); } catch (e) { /* ignore */ }
    }, []);

    // ─── Flow ───────────────────────────────────────────────────────────────
    const startLevel = (i) => {
        const v = pickVariant(LEVELS[i]);
        setIndex(i);
        setVariant(v);
        setItems(buildItems(v));
        setWrongKeys([]);
        setHiddenKey(null);
        setSolved(false);
        setRound((r) => r + 1);
        levelStart.current = Date.now();
    };

    const choose = (item) => {
        if (solved || wrongKeys.includes(item.key) || hiddenKey === item.key) return;
        if (item.isOdd) {
            const firstTry = wrongKeys.length === 0;
            setSolved(true);
            setResults((r) => [...r, firstTry]);
            setStreak((s) => (firstTry ? s + 1 : 0));
            playCorrect();
            const cheer = CHEERS[Math.floor(Math.random() * CHEERS.length)];
            setBubble(cheer);
            say(`${cheer} ${variant.explain}`);
            onComplete?.(level.id, firstTry, Date.now() - levelStart.current);
        } else {
            setWrongKeys((w) => [...w, item.key]);
            setShakeKey(item.key);
            later(() => setShakeKey(null), 450);
            playWrong();
            setBubble('Oops! Try again 🙂');
            say('Oops! Try again.');
        }
    };

    const showHint = () => {
        if (solved || hiddenKey !== null) return;
        const candidates = items.filter((it) => !it.isOdd && !wrongKeys.includes(it.key));
        if (candidates.length > 1) setHiddenKey(candidates[0].key);
        setBubble(`💡 ${variant.hint}`);
        say(variant.hint);
        tone(600, 0, 0.1, 0.15);
    };

    const next = () => {
        if (index + 1 < LEVELS.length) {
            startLevel(index + 1);
        } else {
            const correct = results.filter(Boolean).length;
            setFinished(true);
            playFinish();
            say(correct === LEVELS.length ? 'Perfect! You are a super thinker!' : 'Great thinking! Well done!');
            onFinish?.(correct, LEVELS.length);
        }
    };

    const replay = () => {
        timers.current.forEach(clearTimeout);
        setResults([]);
        setStreak(0);
        setFinished(false);
        startLevel(0);
    };

    const correctCount = results.filter(Boolean).length;

    const styles = `
        .oo-root { position: relative; padding: 12px 16px 16px; color: #3e2723; user-select: none; font-family: 'Baloo 2', 'Fredoka One', 'Comic Sans MS', cursive; overflow: hidden; }
        .oo-top { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
        .oo-trail { flex: 1; display: flex; align-items: center; gap: 4px; overflow-x: auto; padding: 6px 2px; }
        .oo-dot { flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 13px; border: 3px solid #E2E8F0; background: #fff; color: #94A3B8; }
        .oo-dot.ok { background: #22C55E; border-color: #16A34A; color: #fff; }
        .oo-dot.retry { background: #FDE68A; border-color: #F59E0B; color: #92400E; }
        .oo-dot.now { transform: scale(1.18); color: #fff; box-shadow: 0 0 0 4px rgba(255,255,255,.9), 0 0 0 7px var(--c); background: var(--c); border-color: var(--c); }
        .oo-line { flex-shrink: 0; width: 8px; height: 3px; background: #E2E8F0; border-radius: 2px; }
        .oo-stat { flex-shrink: 0; display: flex; align-items: center; gap: 4px; padding: 4px 12px; border-radius: 16px; font-weight: 900; font-size: 15px; background: #FFF8E1; border: 2px solid #FFE0B2; }
        .oo-sound { flex-shrink: 0; width: 40px; height: 40px; border-radius: 50%; border: 3px solid #B3E5FC; background: #E1F5FE; font-size: 18px; cursor: pointer; }

        .oo-host { display: flex; align-items: center; gap: 12px; margin: 0 auto 14px; max-width: 760px; }
        .oo-owl { font-size: 58px; line-height: 1; animation: oo-bob 2s ease-in-out infinite; flex-shrink: 0; cursor: pointer; }
        .oo-bubble {
            position: relative; flex: 1; background: #fff; border: 3px solid #FFD54F; border-radius: 22px; padding: 10px 16px;
            font-weight: 900; font-size: 22px; box-shadow: 0 4px 0 #FFD54F; animation: oo-in .3s ease;
        }
        .oo-bubble::before { content: ''; position: absolute; left: -14px; top: 50%; transform: translateY(-50%); border: 10px solid transparent; border-right-color: #FFD54F; border-left: 0; }
        .oo-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px; }
        .oo-chip { font-size: 12px; font-weight: 900; padding: 1px 10px; border-radius: 10px; color: #fff; }
        .oo-chip.topic { background: #E0E7FF; color: #3730A3; }

        .oo-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; max-width: 760px; margin: 0 auto; }
        .oo-card {
            position: relative; aspect-ratio: 1; max-height: clamp(110px, calc(100vh - 480px), 180px); border-radius: 26px; border: 4px solid #E2E8F0; background: #fff; color: #000;
            display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: clamp(48px, 7vw, 86px);
            box-shadow: 0 6px 0 #E2E8F0, 0 10px 20px rgba(0,0,0,.06); transition: transform .15s, box-shadow .15s, opacity .3s;
            animation: oo-drop .45s cubic-bezier(.34,1.56,.64,1) both;
        }
        .oo-card:not(:disabled):hover { transform: translateY(-6px) rotate(-2deg); box-shadow: 0 12px 0 #E2E8F0, 0 16px 26px rgba(0,0,0,.1); }
        .oo-card:disabled { cursor: default; color: #000; } /* Chrome greys disabled-button text, which also fades color emoji */
        .oo-card.wrong { background: #FEF2F2; border-color: #FCA5A5; box-shadow: 0 6px 0 #FCA5A5; opacity: .55; filter: grayscale(.6); }
        .oo-card.faded { opacity: .15; filter: grayscale(1); transform: scale(.9); }
        .oo-card.right { background: #DCFCE7; border-color: #22C55E; box-shadow: 0 6px 0 #16A34A, 0 0 0 6px #BBF7D0; animation: oo-win .6s ease; }
        .oo-card.dim { opacity: .45; }
        .oo-card.shake { animation: oo-shake .45s ease; }
        .oo-card .oo-badge { position: absolute; top: -12px; right: -12px; width: 34px; height: 34px; border-radius: 50%; background: #22C55E; color: #fff; font-size: 18px; font-weight: 900; display: flex; align-items: center; justify-content: center; border: 3px solid #fff; }
        .oo-card .oo-x { position: absolute; top: -10px; right: -10px; width: 28px; height: 28px; border-radius: 50%; background: #F87171; color: #fff; font-size: 14px; font-weight: 900; display: flex; align-items: center; justify-content: center; border: 3px solid #fff; }
        .oo-spark { position: absolute; font-size: 22px; pointer-events: none; animation: oo-spark .8s ease-out forwards; }

        .oo-bottom { max-width: 760px; margin: 18px auto 0; min-height: 70px; display: flex; align-items: center; justify-content: center; gap: 12px; }
        .oo-btn { min-height: 48px; padding: 0 24px; border-radius: 24px; border: none; font-weight: 900; font-size: 18px; cursor: pointer; font-family: inherit; }
        .oo-btn:active { transform: translateY(2px); }
        .oo-btn:disabled { opacity: .4; cursor: not-allowed; }
        .oo-btn.hint { background: #EFF6FF; color: #1D4ED8; border: 3px solid #93C5FD; box-shadow: 0 4px 0 #93C5FD; }
        .oo-btn.next { background: #16A34A; color: #fff; box-shadow: 0 4px 0 #15803D; font-size: 20px; flex-shrink: 0; animation: oo-pulse 1.4s ease-in-out infinite; }
        .oo-explain { flex: 1; display: flex; align-items: center; gap: 10px; background: #F0FDF4; border: 3px solid #86EFAC; border-radius: 20px; padding: 8px 14px; font-weight: 800; font-size: 16px; animation: oo-in .3s ease; }
        .oo-explain b { font-size: 36px; }

        .oo-final { text-align: center; padding: 0 0 4px; }
        .oo-trophy { font-size: 60px; line-height: 1.1; animation: oo-bob 1.2s ease-in-out infinite; }
        .oo-final-stars { font-size: 36px; line-height: 1.2; margin: 0; }
        .oo-final-stars span { display: inline-block; animation: oo-drop .5s cubic-bezier(.34,1.56,.64,1) both; }
        .oo-recap { display: flex; flex-wrap: wrap; justify-content: center; gap: 6px; margin: 10px auto 12px; max-width: 640px; }
        .oo-recap div { width: 44px; padding: 2px 0; border-radius: 12px; font-size: 18px; border: 3px solid; }
        .oo-confetti { position: absolute; top: -20px; width: 10px; height: 16px; border-radius: 3px; animation: oo-fall linear forwards; pointer-events: none; }

        @media (max-width: 700px) {
            .oo-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); max-width: 380px; }
            .oo-card { max-height: none; }
            .oo-bubble { font-size: 17px; }
            .oo-owl { font-size: 44px; }
            .oo-bottom { flex-wrap: wrap; }
        }

        @keyframes oo-bob { 0%,100% { transform: translateY(0) rotate(-3deg) } 50% { transform: translateY(-6px) rotate(3deg) } }
        @keyframes oo-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
        @keyframes oo-drop { 0% { opacity: 0; transform: translateY(-30px) scale(.7) } 100% { opacity: 1; transform: none } }
        @keyframes oo-shake { 0%,100% { transform: translateX(0) } 20% { transform: translateX(-10px) rotate(-4deg) } 40% { transform: translateX(10px) rotate(4deg) } 60% { transform: translateX(-6px) } 80% { transform: translateX(6px) } }
        @keyframes oo-win { 0% { transform: scale(1) } 40% { transform: scale(1.15) rotate(4deg) } 70% { transform: scale(.97) } 100% { transform: scale(1.05) } }
        @keyframes oo-spark { 0% { opacity: 1; transform: translate(0,0) scale(.5) } 100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1.4) } }
        @keyframes oo-pulse { 0%,100% { transform: scale(1) } 50% { transform: scale(1.06) } }
        @keyframes oo-fall { to { transform: translateY(620px) rotate(720deg) } }
    `;

    const sparkles = [[-60, -50], [60, -50], [-70, 20], [70, 20], [0, -70], [-30, 60], [30, 60]];

    const header = (
        <div className="oo-top">
            <div className="oo-trail">
                {LEVELS.map((l, i) => {
                    const r = results[i];
                    const now = i === index && !finished;
                    const cls = r === true ? 'ok' : r === false ? 'retry' : now ? 'now' : '';
                    return (
                        <React.Fragment key={l.id}>
                            {i > 0 && <span className="oo-line" />}
                            <span className={`oo-dot ${cls}`} style={{ '--c': DIFF_COLOR[l.difficulty] }} title={`${l.topic} · ${l.difficulty}`}>
                                {r === true ? '✓' : r === false ? '★' : l.id}
                            </span>
                        </React.Fragment>
                    );
                })}
            </div>
            <span className="oo-stat" title="First-try answers">⭐ {correctCount}</span>
            <span className="oo-stat" title="Streak">🔥 {streak}</span>
        </div>
    );

    if (finished) {
        const pct = Math.round((correctCount / LEVELS.length) * 100);
        const stars = starsFor(pct);
        return (
            <div className="oo-root">
                <style>{styles}</style>
                {Array.from({ length: 36 }).map((_, i) => (
                    <span key={i} className="oo-confetti" style={{ left: `${(i * 37) % 100}%`, background: CONFETTI[i % CONFETTI.length], animationDuration: `${1.6 + (i % 5) * 0.3}s`, animationDelay: `${(i % 7) * 0.08}s` }} />
                ))}
                {header}
                <div className="oo-final">
                    <div className="oo-trophy">{stars === 3 ? '🏆' : stars >= 1 ? '🦉' : '💪'}</div>
                    <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.2 }}>{stars === 3 ? 'Perfect! Super thinker!' : stars >= 1 ? 'Great thinking!' : 'Good try! Play again!'}</div>
                    <div className="oo-final-stars">
                        {[1, 2, 3].map((s) => <span key={s} style={{ opacity: s <= stars ? 1 : 0.2, animationDelay: `${s * 0.15}s` }}>⭐</span>)}
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#6D4C41' }}>You found {correctCount} of {LEVELS.length} on the first try!</div>
                    <div className="oo-recap">
                        {LEVELS.map((l, i) => (
                            <div key={l.id} title={l.topic} style={{ borderColor: results[i] ? '#22C55E' : '#F59E0B', background: results[i] ? '#DCFCE7' : '#FEF3C7' }}>
                                {results[i] ? '⭐' : '💪'}
                            </div>
                        ))}
                    </div>
                    <button type="button" className="oo-btn next" style={{ animation: 'none' }} onClick={replay}>🔄 Play again</button>
                </div>
            </div>
        );
    }

    return (
        <div className="oo-root">
            <style>{styles}</style>
            {header}

            <div className="oo-host">
                <span className="oo-owl" title="Tap Ollie to hear again" onClick={() => say(solved ? variant.explain : bubble.replace('💡 ', ''))}>🦉</span>
                <div className="oo-bubble" key={bubble}>
                    {bubble}
                    <div className="oo-meta">
                        <span className="oo-chip" style={{ background: DIFF_COLOR[level.difficulty] }}>{level.difficulty}</span>
                        <span className="oo-chip topic">Level {level.id} · {level.topic}</span>
                    </div>
                </div>
            </div>

            <div className="oo-grid" key={round}>
                {items.map((it, i) => {
                    const isWrong = wrongKeys.includes(it.key);
                    const isHidden = hiddenKey === it.key;
                    const isRight = solved && it.isOdd;
                    const cls = [
                        'oo-card',
                        isRight ? 'right' : '',
                        isWrong ? 'wrong' : '',
                        isHidden ? 'faded' : '',
                        solved && !it.isOdd && !isWrong ? 'dim' : '',
                        shakeKey === it.key ? 'shake' : '',
                    ].join(' ');
                    return (
                        <button
                            key={it.key}
                            type="button"
                            className={cls}
                            style={{ animationDelay: isRight || shakeKey === it.key ? '0s' : `${i * 0.08}s` }}
                            disabled={solved || isWrong || isHidden}
                            onClick={() => choose(it)}
                        >
                            {it.emoji}
                            {isRight && <span className="oo-badge">✓</span>}
                            {isWrong && <span className="oo-x">✗</span>}
                            {isRight && sparkles.map(([dx, dy], s) => (
                                <span key={s} className="oo-spark" style={{ '--dx': `${dx}px`, '--dy': `${dy}px` }}>✨</span>
                            ))}
                        </button>
                    );
                })}
            </div>

            <div className="oo-bottom">
                {solved ? (
                    <>
                        <div className="oo-explain">
                            <b>{variant.items[variant.odd]}</b>
                            <span>{variant.explain}</span>
                        </div>
                        <button type="button" className="oo-btn next" onClick={next}>
                            {index + 1 < LEVELS.length ? 'Next ➜' : 'Finish 🏁'}
                        </button>
                    </>
                ) : (
                    <button type="button" className="oo-btn hint" onClick={showHint} disabled={hiddenKey !== null}>
                        💡 {hiddenKey !== null ? 'Hint used' : 'Hint'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default OddOneOut;
